import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { PageLayout, type PageBreadcrumbItem } from "@/components/layout/PageLayout";
import { PageState, PanelState, type PageStateProps } from "@/components/layout/PageState";
import { pageStateAnnouncement } from "@/components/layout/pageStateAnnouncement";
import {
  RecordAlerts,
  type RecordAlert,
  RecordHero,
  type RecordIdentity,
  RecordRouteTabs,
  type RecordTab,
  recordPanelId,
  recordTabId,
  RAIL_TARGETS,
  TemplateBody,
} from "./parts";
import { applyBreadcrumbRules } from "./breadcrumbRules";
import { useClaimPageSubNavSlot } from "@/components/layout/pageSubNavSlot";
import { usePageArrival } from "./usePageArrival";

const CRUMB_TARGETS =
  "[&_nav[aria-label=Breadcrumb]_a]:inline-flex [&_nav[aria-label=Breadcrumb]_a]:min-h-11 [&_nav[aria-label=Breadcrumb]_a]:min-w-11 [&_nav[aria-label=Breadcrumb]_a]:items-center lg:[&_nav[aria-label=Breadcrumb]_a]:min-h-6 lg:[&_nav[aria-label=Breadcrumb]_a]:min-w-6";

export interface RecordPageTemplateProps {
  /** Module name, for the document title "{name} · {module} · Forge" (ACCESSIBILITY.md). */
  module: string;
  /**
   * `Module / Collection / Record`, ending in the record (no href). L2 has no back arrow: the
   * breadcrumb is the back (NAVIGATION_AND_STATES.md). Phones show `‹ Collection` only.
   * Never two crumbs to the same place.
   */
  breadcrumbs: PageBreadcrumbItem[];
  identity: RecordIdentity;
  /** Record actions, right of the identity. */
  actions?: ReactNode;
  /**
   * The alert slot. ANYTHING NEEDING A HUMAN DECISION GOES HERE — never as a field value
   * or a line of body text inside a tab (PAGE_TEMPLATES.md § T2 rules; the "legacy household
   * role needs review" flag is the canonical case). Renders nothing when empty.
   */
  alerts?: RecordAlert[];
  /** Pill tabs; each is a route (`/people/:id/giving`), so a tab change is a URL change. */
  tabs?: RecordTab[];
  activeTab?: string;
  /**
   * The record's own path (`/people/123`). Focus moves to the name and "{name} loaded" is
   * announced when this changes, not when only the tab segment does.
   */
  recordPath: string;
  /** Related records (household, family, giving health). Never form fields. Stacks below the main column under 1280px. */
  rail?: ReactNode;
  /** The rail landmark's name; defaults to "Related to {name}". */
  railLabel?: string;
  /**
   * The record is still loading. Render the same template (with `identity.name` like "Loading
   * person", the real tab links and skeleton children) so nothing jumps when data arrives: the
   * hero shimmers, tabs are real (NAVIGATION_AND_STATES.md § Page states, T2).
   */
  loading?: boolean;
  /**
   * The record can't be shown (NAVIGATION_AND_STATES.md § Page states, T2). The template places
   * it in the tab panel, in place of `children`:
   * - `error`: the record failed to load. The tabs stay real, and the rail and alerts keep
   *   whatever did load.
   * - `forbidden`: the viewer may not see this record. The hero shows the name only (no
   *   photo, status, contact or actions), the tabs are locked, and there are no alerts or rail.
   * T2 has no first-run state, and a filtered state belongs to the tab that filters (use
   * `PanelState` there).
   */
  state?: Extract<PageStateProps, { kind: "error" | "forbidden" }>;
  /** The active tab's content. */
  children: ReactNode;
}

/**
 * T2: one canonical entity with tabs (docs/design/PAGE_TEMPLATES.md § T2, frame 7b).
 *
 * Zones top to bottom: breadcrumbs, identity hero (the page h1), alert slot, pill tabs,
 * then the active tab's content beside a 268px rail of related records.
 *
 * Rules the caller keeps: empty states inside the record are guided and offer the action
 * ("No roles assigned yet. + Assign role", never a bare "None"); history renders through
 * `Timeline`, not a two-column table.
 *
 * @example
 * <RecordPageTemplate
 *   module="Community"
 *   breadcrumbs={[{ label: "People", href: "/people" }, { label: name }]}
 *   identity={{ name, initials, status: { tone: "active", label: "Member" }, contactLine, meta }}
 *   actions={<Button>Edit</Button>}
 *   alerts={[{ id: "legacy-role", tone: "warning", title: "Legacy household role needs review", action }]}
 *   tabs={tabs} activeTab={tab} recordPath={`/people/${id}`}
 *   rail={<HouseholdCard />}
 * >
 *   <DetailsTab />
 * </RecordPageTemplate>
 */
export function RecordPageTemplate({
  module,
  breadcrumbs,
  identity,
  actions,
  alerts,
  tabs,
  activeTab,
  recordPath,
  rail,
  railLabel,
  loading = false,
  state,
  children,
}: RecordPageTemplateProps) {
  const forbidden = state?.kind === "forbidden";
  // An error or no-permission state isn't a successful load: it announces its own heading.
  const stateAnnouncement = pageStateAnnouncement(state);
  // "{name} loaded" is announced when the record is arrived at, or when it finishes loading or
  // recovers from an error or no-permission state (Try again on the same URL), not when only its
  // name changes. Held meanwhile, so it's regenerated with the real name when that state clears.
  const { titleRef, announcement } = usePageArrival(identity.name, recordPath, loading || stateAnnouncement !== null);

  // Record to record (/people/1 → /people/2) the router reuses this page and its scroll
  // container, so the next record would open at the previous one's offset with its heading
  // off screen. Start each record at the top. The first mount already does (a fresh
  // AppLayout), and tab switches keep their place.
  const previousRecord = useRef(recordPath);
  useEffect(() => {
    if (previousRecord.current === recordPath) return;
    previousRecord.current = recordPath;
    const main = document.getElementById("main-content");
    if (main) main.scrollTop = 0;
  }, [recordPath]);
  const idBase = useId();
  // A forbidden record shows no alerts, so it announces none either.
  const alertCount = forbidden ? 0 : alerts?.length ?? 0;
  const alertCountRef = useRef(alertCount);
  useEffect(() => {
    alertCountRef.current = alertCount;
  }, [alertCount]);
  // The alert slot is present on load, which a live region won't reliably read out, so the
  // arrival announcement says how many decisions are waiting. The count is taken once, when
  // the arrival is announced: an alert added or cleared later (fixing the age conflict) is not
  // a new arrival, and RecordAlerts' own live region reports it. Announced once the record
  // has loaded ("Sarah Garcia loaded"), not while it's a placeholder.
  const [liveAnnouncement, setLiveAnnouncement] = useState("");
  // An error or no-permission state isn't a successful load: announce its heading instead.
  useEffect(() => {
    if (loading) {
      setLiveAnnouncement("");
      return;
    }
    if (stateAnnouncement) {
      // Cleared, then set after a beat, like an arrival, so it's heard as a change.
      setLiveAnnouncement("");
      const timer = window.setTimeout(() => setLiveAnnouncement(stateAnnouncement), 100);
      return () => window.clearTimeout(timer);
    }
    if (!announcement) {
      setLiveAnnouncement("");
      return;
    }
    const count = alertCountRef.current;
    setLiveAnnouncement(count > 0
      ? `${announcement}. ${count} ${count === 1 ? "item needs" : "items need"} attention.`
      : announcement);
    // `forbidden` too: a record that becomes forbidden in place must drop the alert count it
    // announced (the alerts are no longer shown).
    // `recordPath` too: record to record (/people/1 → /people/2) with the same failure, nothing
    // else changes, and the second record would get no announcement.
  }, [announcement, loading, forbidden, stateAnnouncement, recordPath]);

  useEffect(() => {
    const previous = document.title;
    document.title = `${identity.name} · ${module} · Forge`;
    return () => {
      document.title = previous;
    };
  }, [identity.name, module]);

  // Phones collapse the trail to `‹ Collection` (SURFACES.md) inside PageLayout (`phoneBackLink`),
  // which needs the whole trail to find the nearest linked crumb. The rules run first, so a
  // caller's href on the current crumb is dropped. Module / Collection / Record, max three.
  const crumbs = applyBreadcrumbRules(breadcrumbs);
  const activeLabel = tabs?.find((tab) => tab.id === activeTab)?.label;
  const hasTabs = Boolean(tabs && tabs.length > 0 && activeTab);
  // A module host's phone section selector: the hero is this page's title, so it sits under it.
  const subNavSlot = useClaimPageSubNavSlot();

  return (
    // headerless: PageLayout draws the breadcrumbs; the hero is the page heading.
    <PageLayout title={identity.name} breadcrumbs={crumbs} phoneBackLink topbarBreadcrumbs headerless className={CRUMB_TARGETS}>
      <RecordHero
        // No permission: the name only (NAVIGATION_AND_STATES.md, T2 "Hero shows name only").
        identity={forbidden ? { name: identity.name } : identity}
        actions={forbidden ? undefined : actions}
        titleRef={titleRef}
        loading={loading}
      />
      {subNavSlot}
      <RecordAlerts alerts={loading || forbidden ? [] : alerts ?? []} />
      {hasTabs
        ? (
          // Keyed by record: the roving tab stop starts fresh on each record, even when the
          // router reuses this template (same active tab on /people/1 and /people/2).
          <RecordRouteTabs
            key={recordPath}
            tabs={tabs}
            activeTab={activeTab}
            label={`${identity.name} sections`}
            idBase={idBase}
            locked={forbidden}
          />
        )
        : null}
      <TemplateBody
        rail={forbidden ? undefined : rail}
        railLabel={railLabel ?? `Related to ${identity.name}`}
        // Rail links and buttons: 44px targets below 1024px, 24px above (ACCESSIBILITY.md § Target size).
        railClassName={RAIL_TARGETS}
      >
        {hasTabs ? (
          <section
            role="tabpanel"
            id={recordPanelId(idBase)}
            // Every tab's aria-controls points here, so the panel exists whenever tabs do. It's
            // labelled by the selected tab, or by the record if a caller's activeTab isn't listed.
            {...(activeLabel
              ? { "aria-labelledby": recordTabId(idBase, activeTab) }
              : { "aria-label": `${identity.name} section` })}
            // Focusable, so Tab from the tab list lands in the panel even when a tab (Activity)
            // has no controls of its own (ACCESSIBILITY.md § T2).
            tabIndex={0}
            className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {/* The section's heading: h1 is the record, tab content starts at h3 (ACCESSIBILITY.md). */}
            {activeLabel ? <h2 className="sr-only">{activeLabel}</h2> : null}
            {/* Panel scale inside a tab (NAVIGATION_AND_STATES.md § Components), under the
                section's h2, so it heads at h3. */}
            {/* With an unmatched activeTab there's no section h2, so the state takes h2 itself. */}
            {state ? <PanelState {...state} headingLevel={activeLabel ? 3 : 2} /> : children}
          </section>
        ) : (
          <section>{state ? <PageState {...state} /> : children}</section>
        )}
      </TemplateBody>
      {/* Last, so PageLayout's space-y gap doesn't open above the hero. */}
      <div role="status" aria-live="polite" className="sr-only">{liveAnnouncement}</div>
    </PageLayout>
  );
}
